import { useDispatch, useSelector } from 'react-redux';
import { addHistoryEntry, toggleLoader } from '../store/actions';
import { generateLLMResponse } from '../services/generateLLMResponse';
import { getAbortController } from '../utils/abortController'
/**
 * Custom hook to generate an LLM response and update the history.
 * 
 * @returns {Function} - The function to generate the LLM response.
 * 
 * @example
 * const generateLLMResponse = useGenerateLLMResponse();
 * const prompt = "How do I apply for a new job?";
 * generateLLMResponse(prompt)
 *   .then(data => console.log('Generated data:', data))
 *   .catch(error => console.error('Error:', error));
 */

export function useGenerateLLMResponse() {
  const dispatch = useDispatch();
  const history = useSelector((state) => state.history);

  /**
   * Generates a response from the LLM based on the provided prompt and optional instructions,
   * processes the returned model data, preprocesses the JSON BPMN,
   * and dispatches a new history entry to the Redux store.
   *
   * @param {string} prompt - The user prompt to send to the LLM.
   * @param {string|null} instructions - Optional instructions to guide the LLM's response.
   * @returns {Promise<string>} - The raw JSON data string returned by the LLM.
   * @throws {Error} - Throws if the LLM response fails or processing encounters an error.
   */
  const handleGenerateLLMResponse = async (prompt, instructions = null) => {
    const abortController = getAbortController();
    dispatch(toggleLoader("assistant", true));
    try {
      // Fetch LLM response with abort signal
      const completion = await generateLLMResponse(history, prompt, instructions, abortController.signal);

      // Abort-safe guard
      if (!completion || abortController.signal.aborted) {
        console.warn("Operation aborted mid-process.");
        return;
      }
      
      const jsonData = completion.model;
      const currentIndex = history.entries.length;

      // Dispatch action to add a new history entry
      dispatch(addHistoryEntry({ referencedIndex: history.pointer, currentIndex, prompt, jsonData}));
      delete completion.model;
      return jsonData; 
    } catch (error) {
      if (
        !(
          (error && error.name === 'AbortError') ||
          (error && error.code === 'ERR_CANCELED')
        )
      ) {
        console.error("Error generating LLM response:", error);
      }
      throw error;
    } finally {
      dispatch(toggleLoader("assistant", false));
    }
  }

  return handleGenerateLLMResponse;
}